import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import queryString from 'query-string';
import moment from 'moment';
import 'moment/locale/ru';
import SearchBar from './components/SearchBar';
import Legend from './components/Legend';
import WeekNav from './components/WeekNav';
import ViewToggle from './components/ViewToggle';
import WeekView from './components/WeekView';
import MonthView from './components/MonthView';
import ShiftModal from './components/ShiftModal';
import { OperatorInfoModal, LoadingStub } from '../_Components';
import {
    GET_LIST,
    UPDATE,
    CREATE,
    DELETE,
    provider,
    history
} from '../_utils';
import Utils, {
    toWeekInterval,
    toMonthInterval,
    scheduleRangeForView,
    monthRangeLabel,
    planForDate,
    shiftKindKey,
} from './utils';
import './weekView.css';

moment.locale('ru');

const readStoredFilter = () => {
    const { filter } = queryString.parse(history.location.search);
    let parsed = {};
    if (filter) {
        try {
            parsed = JSON.parse(filter);
        } catch (error) {
            console.error('Error parsing filter from URL:', error);
        }
    } else {
        try {
            const saved = localStorage.getItem('schedule_filter');
            if (saved) parsed = JSON.parse(saved);
        } catch (error) {
            console.error('Error restoring filter from localStorage:', error);
        }
    }
    const start = parsed.interval && parsed.interval.startDate
        ? moment(parsed.interval.startDate)
        : moment();
    const view = parsed.view === 'month' ? 'month' : 'week';
    const anchor = parsed.interval && parsed.interval.anchor
        ? moment(parsed.interval.anchor)
        : start;
    return {
        view,
        interval: view === 'month' ? toMonthInterval(anchor) : toWeekInterval(start),
        hideEmpty: parsed.hideEmpty !== undefined ? parsed.hideEmpty : true,
    };
};

const Schedule = () => {
    const stored = useRef(readStoredFilter()).current;
    const mounted = useRef(false);
    const [view, setView] = useState(stored.view);
    const [interval, setInterval] = useState(stored.interval);
    const [hideEmpty, setHideEmpty] = useState(stored.hideEmpty);
    const [operators, setOperators] = useState([]);
    const [data, setData] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [kindFilter, setKindFilter] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [operatorInfoOpen, setOperatorInfoOpen] = useState(false);
    const [selectedOperatorId, setSelectedOperatorId] = useState(null);
    const [ready, setReady] = useState(false);

    const persistFilter = useCallback((nextInterval, nextHideEmpty, nextView = view) => {
        try {
            const filterObj = {
                view: nextView,
                interval: {
                    startDate: moment(nextInterval.startDate).format('YYYY-MM-DD'),
                    endDate: moment(nextInterval.endDate).format('YYYY-MM-DD'),
                    ...(nextInterval.anchor ? { anchor: nextInterval.anchor } : {}),
                },
                hideEmpty: nextHideEmpty,
            };
            const filterString = JSON.stringify(filterObj);
            localStorage.setItem('schedule_filter', filterString);
            history.push({
                pathname: history.location.pathname,
                search: `?${queryString.stringify({ filter: filterString })}`,
            });
        } catch (error) {
            console.error('Error updating URL:', error);
        }
    }, [view]);

    const fetchOperators = useCallback(async () => {
        const { data: list } = await provider(GET_LIST, '/operators', {
            filter: { status: 'Active', type: { $in: ['operator', 'leader', 'senior', 'test'] } },
            sort: { field: 'login', order: 'ASC' },
            pagination: { page: 1, perPage: 1000 },
        });
        if (!mounted.current || !list) return;
        setOperators(list.map((item) => Utils.formatOperator(item)).sort((a, b) =>
            (a.fullname || a.name || '').localeCompare(b.fullname || b.name || '', 'ru')
        ));
    }, []);

    const fetchData = useCallback(async (nextInterval, nextView) => {
        const range = scheduleRangeForView(nextInterval, nextView);
        const { data: scheduleData } = await provider(GET_LIST, '/schedule', {
            filter: { startDate: range.startDate, endDate: range.endDate },
            sort: { field: 'date', order: 'DESC' },
            pagination: { page: 1, perPage: 10000 },
        });
        if (!mounted.current || !scheduleData) return;
        setData(scheduleData);
    }, []);

    const fetchHolidays = useCallback(async (nextInterval) => {
        try {
            const { data: list } = await provider(GET_LIST, '/holidays', {
                filter: {
                    date: {
                        $gte: moment(nextInterval.startDate).startOf('day').toDate(),
                        $lte: moment(nextInterval.endDate).endOf('day').toDate(),
                    },
                },
                sort: { field: 'date', order: 'ASC' },
                pagination: { page: 1, perPage: 1000 },
            });
            if (mounted.current) setHolidays(list || []);
        } catch (error) {
            console.warn('Holidays endpoint not available:', error);
            if (mounted.current) setHolidays([]);
        }
    }, []);

    const fetchCampaigns = useCallback(async () => {
        try {
            const { data: list } = await provider(GET_LIST, '/campaigns', {
                filter: { status: 'Active' },
                sort: { field: 'name', order: 'DESC' },
                pagination: { page: 1, perPage: 1000 },
            });
            if (mounted.current) setCampaigns(list || []);
        } catch (error) {
            if (mounted.current) setCampaigns([]);
        }
    }, []);

    const loadPeriod = useCallback(async (nextInterval, nextHideEmpty = hideEmpty, persist = true, nextView = view) => {
        setInterval(nextInterval);
        setView(nextView);
        if (persist) persistFilter(nextInterval, nextHideEmpty, nextView);
        await Promise.all([
            fetchData(nextInterval, nextView),
            fetchHolidays(nextInterval),
        ]);
    }, [fetchData, fetchHolidays, hideEmpty, persistFilter, view]);

    useEffect(() => {
        mounted.current = true;
        const boot = async () => {
            try {
                await Promise.all([
                    fetchOperators(),
                    fetchCampaigns(),
                    fetchData(stored.interval, stored.view),
                    fetchHolidays(stored.interval),
                ]);
                persistFilter(stored.interval, stored.hideEmpty, stored.view);
            } catch (error) {
                console.error('Error loading schedule:', error);
            } finally {
                if (mounted.current) setReady(true);
            }
        };
        boot();
        return () => { mounted.current = false; };
    }, [fetchOperators, fetchCampaigns, fetchData, fetchHolidays, persistFilter, stored]);

    const dates = useMemo(() => {
        const start = moment(interval.startDate).startOf('day');
        const end = moment(interval.endDate).startOf('day');
        if (view === 'month') {
            const list = [];
            const cursor = start.clone();
            while (cursor.isSameOrBefore(end, 'day')) {
                list.push(cursor.toDate());
                cursor.add(1, 'day');
            }
            return list;
        }
        const weekStart = start.clone().startOf('isoWeek');
        return Array.from({ length: 7 }, (_, index) => weekStart.clone().add(index, 'days').toDate());
    }, [interval, view]);

    const planByDate = useMemo(
        () => dates.map((date) => planForDate(campaigns, date, holidays)),
        [campaigns, dates, holidays]
    );

    const visibleDateKeys = useMemo(
        () => new Set(dates.map((date) => moment(date).format('YYYY-MM-DD'))),
        [dates]
    );

    const kindCounts = useMemo(() => {
        const counts = {};
        data.forEach((shift) => {
            const shiftDate = moment((shift.schedules && shift.schedules[0] && shift.schedules[0].date) || shift.date).format('YYYY-MM-DD');
            if (view === 'week' && !visibleDateKeys.has(shiftDate)) return;
            const key = shiftKindKey(shift);
            if (key) counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
    }, [data, view, visibleDateKeys]);

    const handleClose = useCallback(() => {
        setSelectedOperator(null);
        setSelectedDate(null);
        setSelectedItem(null);
        setModalOpen(false);
    }, []);

    const handleSave = useCallback(async (payload) => {
        try {
            const itemId = selectedItem && (selectedItem.id || selectedItem._id);
            if (itemId) {
                await provider(UPDATE, '/schedule', { id: itemId, data: payload });
            } else {
                await provider(CREATE, '/schedule', { data: payload });
            }
            handleClose();
            fetchData(interval, view);
        } catch (error) {
            console.error('Error saving schedule:', error);
        }
    }, [selectedItem, handleClose, fetchData, interval, view]);

    const handleDelete = useCallback(async (id) => {
        try {
            if (id) await provider(DELETE, '/schedule', { id });
            handleClose();
            fetchData(interval, view);
        } catch (error) {
            console.error('Error deleting schedule:', error);
        }
    }, [handleClose, fetchData, interval, view]);

    const handleCellClick = useCallback((operator, date, shift) => {
        setSelectedOperator(operator);
        setSelectedDate(date);
        setSelectedItem(shift || null);
        setModalOpen(true);
    }, []);

    const setPeriod = useCallback((delta) => {
        if (view === 'month') {
            const start = moment(interval.anchor || interval.startDate).startOf('month').add(delta, 'months');
            loadPeriod(toMonthInterval(start), hideEmpty, true, 'month');
            return;
        }
        const start = moment(interval.startDate).startOf('isoWeek').add(delta, 'weeks');
        loadPeriod(toWeekInterval(start), hideEmpty, true, 'week');
    }, [interval, hideEmpty, loadPeriod, view]);

    const setToday = useCallback(() => {
        if (view === 'month') {
            loadPeriod(toMonthInterval(moment()), hideEmpty, true, 'month');
            return;
        }
        loadPeriod(toWeekInterval(moment()), hideEmpty, true, 'week');
    }, [hideEmpty, loadPeriod, view]);

    const switchToWeek = useCallback(() => {
        const anchor = interval.anchor || interval.startDate;
        const pivot = view === 'month' && moment().isSame(anchor, 'month')
            ? moment()
            : anchor;
        loadPeriod(toWeekInterval(pivot), hideEmpty, true, 'week');
    }, [interval, hideEmpty, loadPeriod, view]);

    const switchToMonth = useCallback(() => {
        const today = moment();
        const start = moment(interval.startDate);
        const end = moment(interval.endDate);
        const pivot = today.isBetween(start, end, 'day', '[]')
            ? today
            : (interval.anchor || interval.startDate);
        loadPeriod(toMonthInterval(pivot), hideEmpty, true, 'month');
    }, [interval, hideEmpty, loadPeriod]);

    const onHideEmpty = useCallback((checked) => {
        setHideEmpty(checked);
        persistFilter(interval, checked);
    }, [interval, persistFilter]);

    const toggleKind = useCallback((key) => {
        setKindFilter((current) => (
            current.includes(key) ? current.filter((item) => item !== key) : current.concat(key)
        ));
    }, []);

    return (
        <Fragment>
            <div className="pc-page pc-sched">
                <div className="pc-sched-topbar">
                    <h1>Расписание</h1>
                    <ViewToggle
                        mode={view}
                        onDay={() => {
                            const today = moment();
                            let target;
                            if (view === 'month') {
                                const month = moment(interval.anchor || interval.startDate);
                                target = today.isSame(month, 'month') ? today : month.clone().startOf('month');
                            } else {
                                const start = moment(interval.startDate);
                                const end = moment(interval.endDate);
                                target = today.isBetween(start, end, 'day', '[]') ? today : start;
                            }
                            history.push(`/schedule/${target.format('YYYY-MM-DD')}/show`);
                        }}
                        onWeek={switchToWeek}
                        onMonth={switchToMonth}
                    />
                    <WeekNav
                        interval={interval}
                        label={view === 'month' ? monthRangeLabel(interval.anchor || interval.startDate) : undefined}
                        onPrev={() => setPeriod(-1)}
                        onNext={() => setPeriod(1)}
                        onToday={setToday}
                        todayLabel={view === 'month' ? 'Текущий' : 'Сегодня'}
                        prevLabel={view === 'month' ? 'Предыдущий месяц' : 'Предыдущая неделя'}
                        nextLabel={view === 'month' ? 'Следующий месяц' : 'Следующая неделя'}
                    />
                    <SearchBar value={searchQuery} onSearch={setSearchQuery} placeholder={view === 'month' ? 'Поиск' : undefined} />
                    <label className="pc-sched-tgl">
                        <input
                            type="checkbox"
                            checked={hideEmpty}
                            onChange={(event) => onHideEmpty(event.target.checked)}
                        />
                        <span className="tr" />
                        Скрыть без смен
                    </label>
                    <div className="pc-sched-end">
                        <Legend counts={kindCounts} active={kindFilter} onToggle={toggleKind} />
                        <button
                            type="button"
                            className="pc-sched-btn pc-sched-btn--pri"
                            onClick={() => {
                                setSelectedOperator(null);
                                setSelectedDate(null);
                                setSelectedItem(null);
                                setModalOpen(true);
                            }}
                        >
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="2" y="3" width="12" height="11" rx="1.5" />
                                <path d="M2 6.5h12M5.5 2v2.5M10.5 2v2.5" />
                            </svg>
                            Смена
                        </button>
                    </div>
                </div>
                <div className="pc-sched-gridwrap">
                    {!ready ? (
                        <LoadingStub title="Загружаем расписание" />
                    ) : view === 'month' ? (
                        <MonthView
                            operators={operators}
                            schedule={data}
                            dates={dates}
                            monthAnchor={interval.anchor || interval.startDate}
                            holidays={holidays}
                            planByDate={planByDate}
                            searchQuery={searchQuery}
                            kindFilter={kindFilter}
                            hideEmpty={hideEmpty}
                            onCellClick={handleCellClick}
                            onWeekClick={(date) => loadPeriod(toWeekInterval(date), hideEmpty, true, 'week')}
                            onOperatorClick={(id) => {
                                setSelectedOperatorId(id);
                                setOperatorInfoOpen(true);
                            }}
                        />
                    ) : (
                        <WeekView
                            operators={operators}
                            schedule={data}
                            weekDates={dates}
                            onCellClick={handleCellClick}
                            searchQuery={searchQuery}
                            holidays={holidays}
                            onOperatorClick={(id) => {
                                setSelectedOperatorId(id);
                                setOperatorInfoOpen(true);
                            }}
                            kindFilter={kindFilter}
                            hideEmpty={hideEmpty}
                        />
                    )}
                </div>
            </div>
            <ShiftModal
                open={modalOpen}
                operator={selectedOperator}
                operators={operators}
                date={selectedDate}
                weekDates={view === 'month'
                    ? dates.filter((item) => moment(item).isSame(interval.anchor || interval.startDate, 'month'))
                    : dates}
                shift={selectedItem}
                onClose={handleClose}
                onSave={handleSave}
                onDelete={handleDelete}
            />
            <OperatorInfoModal
                open={operatorInfoOpen}
                onClose={() => {
                    setOperatorInfoOpen(false);
                    setSelectedOperatorId(null);
                }}
                operatorId={selectedOperatorId}
            />
        </Fragment>
    );
};

export default Schedule;
